// we make this a class so that we can get object.keys without actually creating a object of that interface.
// we can do object.keys on an uninstantiated class

import { logger } from "../../../utilities/logger";
import { IMailSendHeaders } from "../interfaces/IMailSendPayload";
import { normalizeEmailSubject } from "../components/helpers/stringHelper";

class IEmailMapper {
    public contentType: string = "Email, E-mail";
    public subject: string = "Subject, EmailSubject, Email onderwerp";
    public from: string = "EmailFrom, From, EmailSender, Afzender";
    public date: string = "Date, EmailDate, Email datum";
    public received: string = "EmailReceived, Received";
    public to: string = "To, EmailTo, Ontvanger(s)";
    public bcc: string = "";
    public cc: string = "EmailCc, Cc, Ontvanger(s) cc";
    public categories: string = "Categories";
    public conversationIndex: string = "ConversationIndex, ConversationID";
    //public conversationTopic: string = "ConversationTopic";
    public attachments: string = "Attachments, HasAttachments, WithAttachments";
    //public inReplyTo: string = "InReplyTo, EmailInReplyTo, In-Reply-To";
    public messageId: string = "MessageID";
    //public references: string = "References, EmailReferences";
    public replyTo: string = "ReplyTo, EmailReplyTo, Reply-To";
    public importance: string = "Importance";
    public originalSubject: string = "OriginalSubject";
}

export const emailMappingTypes = new IEmailMapper();

interface IEmailColumn {
    name: string, //name is the email property attribute name obtained from the mailbox item
    value: string, //value is the corresponding mapping to a sharepoint column
}

export interface IMappedValue {
    value: any,
    type: string,
}

interface ISpFieldsPerMailCategory {
    category: string,
    fields: string[],
}

class EmailMapper extends IEmailMapper {

    private fieldColumnMap: IEmailColumn[] = []; // the column names we will search for when mapping email contents to meta data fields
    private fieldSharePointColumns: ISpFieldsPerMailCategory[] = [];
    private allSharePointColumns: string[] = []; // "column1, column2" -> ["column1", "column2"]
    private exchangeEmail: any = null;

    constructor() {
        super();
        const keys = Object.keys(emailMappingTypes);
        this.fieldColumnMap = keys.map(key => {
            return ({
                name: key,
                value: this[key],
            })
        });
        this.updateMappingLists();
    }

    public updateMappingLists() {
        let sharePointColumnNames: string[] = [];
        const fieldSharePointColumnMap: ISpFieldsPerMailCategory[] = [];
        this.fieldColumnMap.forEach(emailColumn => {
            const spColumns = emailColumn.value?.split(",").map(c => c.trim()).filter(c => c);

            sharePointColumnNames = sharePointColumnNames.concat(spColumns);
            fieldSharePointColumnMap.push({
                category: emailColumn?.name,
                fields: spColumns
            });
        });
        this.allSharePointColumns = sharePointColumnNames;
        this.fieldSharePointColumns = fieldSharePointColumnMap;
    }

    public getColumnValue = (columnName: string): string => {
        if (this.fieldColumnMap) {
            const index = this.fieldColumnMap.findIndex(f => f.name.toLowerCase() === columnName.toLowerCase());
            //returns -1 if no value has been saved.
            return index >= 0 ? this.fieldColumnMap[index].value : null;
        }
        return null;
    }

    public setColumnNameValue = (columnName: string, value: string) => {
        this.fieldColumnMap[this.fieldColumnMap.findIndex(f => f.name === columnName)].value = value;
    }

    public updateValuesFromExchange = (exchangeData: any) => {
        if (exchangeData) {
            this.exchangeEmail = exchangeData;
        }
    }

    public findMappedColumnNames = (fieldName: string): string[] => {
        return this.fieldColumnMap.find(f => f.name.toLowerCase() === fieldName.toLowerCase())?.value?.split(",").map(s => s.trim());
    }

    public setMapping = (jsonMappingString: string) => {
        if (jsonMappingString) {
            try {
                const mappingObject = JSON.parse(jsonMappingString);
                this.fieldColumnMap = mappingObject;
                this.updateMappingLists();
            } catch (error) {
                logger(error, "EmailMapper.ts (setMapping)");
            }
        }
    }

    public getEmailMappingString = () => {
        return JSON.stringify(this.fieldColumnMap);
    }

    public getSpToEmailMapping = async (spColumnName: string, mailbox: Office.Mailbox, mailSendHeaders?: IMailSendHeaders): Promise<IMappedValue> => {
        let mappedVal: IMappedValue = { value: null, type: null };
        try {
            const mailPropertyName = this.fieldSharePointColumns.find(fieldsPerCat => {
                return fieldsPerCat.fields.some(field => field === spColumnName);
            })?.category;

            if (mailPropertyName && (mailbox || mailSendHeaders)) {
                switch (mailPropertyName.toLowerCase()) {
                    case "subject":
                        mappedVal.value = mailbox?.item?.normalizedSubject ?? mailSendHeaders?.subject;
                        break;
                    case "from":
                        mappedVal.value = mailbox?.item?.from?.emailAddress ?? mailSendHeaders?.from; //email address? display name
                        break;
                    case "date":
                        let sentDate: Date | undefined = this.exchangeEmail?.sentDateTime ?? this.exchangeEmail?.SentDateTime ?? mailSendHeaders?.date;
                        try {
                            if (!sentDate && typeof mailbox?.item?.getAllInternetHeadersAsync === "function") {
                                sentDate = await new Promise<Date | undefined>((resolve) => {
                                    mailbox.item?.getAllInternetHeadersAsync((asyncResult) => {
                                        if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                                            const headers = asyncResult.value as string;
                                            const dateMatch = headers?.match(/^Date:\s*(.+)$/im);
                                            const dateString = dateMatch?.[1]?.trim();
                                            resolve(dateString ? new Date(dateString) : undefined);
                                        } else {
                                            resolve(undefined);
                                        }
                                    })
                                });
                            }
                        } catch (error) {
                            // No-op
                        }

                        if (!sentDate) {
                            sentDate = mailbox?.item?.dateTimeCreated;
                        }

                        mappedVal.value = sentDate;
                        break;
                    case "received":
                        mappedVal.value = this.exchangeEmail?.receivedDateTime ?? this.exchangeEmail?.ReceivedDateTime;
                        break;
                    case "to":
                        let toRecipients: string[] | undefined;
                        if (Array.isArray(mailSendHeaders?.to)) {
                            toRecipients = mailSendHeaders?.to;
                        }
                        else {
                            try {
                                if (Array.isArray(mailbox?.item?.to)) {
                                    toRecipients = mailbox?.item?.to.map(t => t.emailAddress);
                                }
                                else if (typeof mailbox?.item?.to === "object") {
                                    toRecipients = await new Promise<string[]>((resolve) => {
                                        mailbox.item?.to.getAsync((result) => {
                                            if (result.status === Office.AsyncResultStatus.Succeeded) {
                                                const toEmailAddress = result.value?.map(v => v.emailAddress);
                                                resolve(toEmailAddress);
                                            } else {
                                                resolve(undefined);
                                            }
                                        })
                                    });
                                }
                            } catch (toRecipientsError) {
                                logger(toRecipientsError, "EmailMapper.ts (toRecipients)");
                            }
                        }
                        mappedVal.value = toRecipients;
                        break;
                    case "bcc":
                        let bccRecipients: string[] | undefined;
                        if (Array.isArray(mailSendHeaders?.bcc)) {
                            bccRecipients = mailSendHeaders?.bcc;
                        }
                        else {
                            try {
                                if (Array.isArray(mailbox?.item?.bcc)) {
                                    bccRecipients = mailbox?.item?.bcc.map(t => t.emailAddress);
                                }
                                else if (typeof mailbox?.item?.bcc === "object") {
                                    bccRecipients = await new Promise<string[]>((resolve) => {
                                        mailbox?.item?.bcc.getAsync((result) => {
                                            if (result.status === Office.AsyncResultStatus.Succeeded) {
                                                const toEmailAddress = result.value?.map(v => v.emailAddress);
                                                resolve(toEmailAddress);
                                            } else {
                                                resolve(undefined);
                                            }
                                        })
                                    });
                                }
                            } catch (bccError) {
                                logger(bccError, "EmailMapper.ts (bcc)");
                            }
                        }
                        mappedVal.value = bccRecipients;
                        break;
                    case "cc":
                        let ccRecipients: string[] | undefined;
                        if (Array.isArray(mailSendHeaders?.cc)) {
                            ccRecipients = mailSendHeaders?.cc;
                        }
                        else {
                            try {
                                if (Array.isArray(mailbox?.item?.cc)) {
                                    ccRecipients = mailbox?.item?.cc.map(t => t.emailAddress);
                                }
                                else if (typeof mailbox?.item?.cc === "object") {
                                    ccRecipients = await new Promise<string[]>((resolve) => {
                                        mailbox?.item?.cc.getAsync((result) => {
                                            if (result.status === Office.AsyncResultStatus.Succeeded) {
                                                const toEmailAddress = result.value?.map(v => v.emailAddress);
                                                resolve(toEmailAddress);
                                            } else {
                                                resolve(undefined);
                                            }
                                        })
                                    });
                                }
                            } catch (ccError) {
                                logger(ccError, "EmailMapper.ts (cc)");
                            }
                        }
                        mappedVal.value = ccRecipients;
                        break;
                    case "categories":
                        let categories: string[] | undefined;
                        if (Array.isArray(mailSendHeaders?.categories) && mailSendHeaders?.categories.length > 0) {
                            categories = mailSendHeaders?.categories;
                        }
                        try {
                            if (mailbox?.item?.categories) {
                                categories = await new Promise<string[]>((resolve) => {
                                    mailbox.item?.categories.getAsync((result) => {
                                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                                            const categories = result.value?.map(c => c.displayName);
                                            resolve(categories);
                                        } else {
                                            resolve(undefined)
                                        }
                                    });
                                });
                            }
                        } catch (catError) {
                            logger(catError, "EmailMapper.ts (categories)");
                        }
                        mappedVal.value = categories;
                        break;
                    case "conversationIndex".toLowerCase():
                        mappedVal.value = this.exchangeEmail?.conversationIndex ?? this.exchangeEmail?.ConversationIndex ?? mailSendHeaders?.conversationIndex;
                        break;
                    case "conversationTopic".toLowerCase():
                        mappedVal.value = mailbox?.item?.conversationId ?? mailSendHeaders?.conversationId;
                        break;
                    case "attachments":
                        mappedVal.value = this.exchangeEmail?.hasAttachments?.toString() ?? this.exchangeEmail?.HasAttachments?.toString() ?? mailSendHeaders?.hasAttachments?.toString();
                        break;
                    case "inReplyTo".toLowerCase():
                        mappedVal.value = null; //??
                        break;
                    case "messageId".toLowerCase():
                        mappedVal.value = mailbox?.item?.internetMessageId ?? mailSendHeaders?.internetMessageId;
                        break;
                    case "references":
                        mappedVal.value = null; //??
                        break;
                    case "replyTo".toLowerCase():
                        mappedVal.value = this.exchangeEmail?.replyTo?.map(v => v.EmailAddress.Address) ?? this.exchangeEmail?.ReplyTo?.map(v => v.EmailAddress.Address);
                        break;
                    case "importance":
                        mappedVal.value = this.exchangeEmail?.importance ?? this.exchangeEmail?.Importance ?? mailSendHeaders?.importance;
                        break;
                    case "originalSubject".toLowerCase():
                        mappedVal.value = mailbox?.item?.subject ?? mailSendHeaders?.subject;
                        break;
                    default:
                        mappedVal.value = null;
                        break;
                }
            }
        } catch (err) {
            logger(err, "EmailMapper.ts (getSpToEmailMapping)");
        }

        mappedVal.type = typeof mappedVal.value;
        return mappedVal;
    }

    public getOfficeJSToEmailMapping = (spColumnName: string, mailboxItem?: any): { mappedVal: IMappedValue, mailPropertyName: string } => {
        let mappedVal: IMappedValue = { value: null, type: null };
        let mailPropertyName = "";
        try {
            mailPropertyName = this.fieldSharePointColumns.find(fieldsPerCat => {
                return fieldsPerCat.fields.some(field => field === spColumnName);
            })?.category;

            if (mailPropertyName && mailboxItem) {
                switch (mailPropertyName.toLowerCase()) {
                    case "subject":
                        mappedVal.value = normalizeEmailSubject(mailboxItem?.subject);
                        break;
                    case "from":
                        // `from` and `sender` differ for send-on-behalf mail; the
                        // Office.js path maps item.from, so prefer `from` here too.
                        mappedVal.value = mailboxItem?.from?.emailAddress?.address ?? mailboxItem?.sender?.emailAddress?.address;
                        break;
                    case "date":
                        mappedVal.value = mailboxItem?.sentDateTime;
                        break;
                    case "received":
                        mappedVal.value = mailboxItem?.receivedDateTime;
                        break;
                    case "to":
                        mappedVal.value = mailboxItem?.toRecipients?.map(t => t.emailAddress?.address);
                        break;
                    case "bcc":
                        mappedVal.value = mailboxItem?.bccRecipients?.map(bcc => bcc.emailAddress?.address);
                        break;
                    case "cc":
                        mappedVal.value = mailboxItem?.ccRecipients?.map(cc => cc.emailAddress?.address);
                        break;
                    case "categories":
                        mappedVal.value = mailboxItem?.categories?.map(cat => cat);
                        break;
                    case "conversationindex":
                        mappedVal.value = mailboxItem?.conversationIndex;
                        break;
                    case "conversationtopic":
                        mappedVal.value = mailboxItem?.conversationId;
                        break;
                    case "attachments":
                        mappedVal.value = mailboxItem?.hasAttachments?.toString();
                        break;
                    case "inreplyto":
                        mappedVal.value = null; //??
                        break;
                    case "messageid":
                        mappedVal.value = mailboxItem?.internetMessageId;
                        break;
                    case "references":
                        mappedVal.value = null; //??
                        break;
                    case "replyto":
                        mappedVal.value = mailboxItem?.replyTo?.map(v => v.emailAddress?.address);
                        break;
                    case "importance":
                        mappedVal.value = mailboxItem?.importance;
                        break;
                    case "originalSubject".toLowerCase():
                        mappedVal.value = mailboxItem?.subject;
                        break;
                    default:
                        mappedVal.value = null;
                        break;
                }
            }
        } catch (err) {
            logger(err, "EmailMapper.ts (getOfficeJSToEmailMapping)");
        }

        mappedVal.type = typeof mappedVal.value;

        return {
            mappedVal, mailPropertyName
        };
    }

    public getAllListedColumns = (): string[] => {
        return this.allSharePointColumns;
    }

    public emailMappingCategoryNameResolver = (languageStrings: any, key: string): string => {
        let translation: string;
        switch (key) {
            case "contentType":
                translation = languageStrings.ContentType ?? "No localization contentType";
                break;
            case "subject":
                translation = languageStrings.Subject ?? "No localization subject";
                break;
            case "from":
                translation = languageStrings.From ?? "No localization from";
                break;
            case "date":
                translation = languageStrings.Date ?? "No localization date";
                break;
            case "received":
                translation = languageStrings.Received ?? "No localization received";
                break;
            case "to":
                translation = languageStrings.To ?? "No localization to";
                break;
            case "bcc":
                translation = languageStrings.Bcc ?? "No localization bcc";
                break;
            case "cc":
                translation = languageStrings.Cc ?? "No localization cc";
                break;
            case "categories":
                translation = languageStrings.Categories ?? "No localization categories";
                break;
            case "conversationIndex":
                translation = languageStrings.ConversationIndex ?? "No localization conversationIndex";
                break;
            case "conversationTopic":
                translation = languageStrings.ConversationTopic ?? "No localization conversationIndex";
                break;
            case "attachments":
                translation = languageStrings.Attachments ?? "No localization attachments";
                break;
            case "inReplyTo":
                translation = languageStrings.InReplyTo ?? "No localization inReplyTo";
                break;
            case "messageId":
                translation = languageStrings.MessageId ?? "No localization messageId";
                break;
            case "references":
                translation = languageStrings.References ?? "No localization references";
                break;
            case "replyTo":
                translation = languageStrings.ReplyTo ?? "No localization replyTo";
                break;
            case "importance":
                translation = languageStrings.Importance ?? "No localization importance";
                break;
            case "originalSubject":
                translation = languageStrings.OriginalSubject ?? "No localization originalSubject";
                break;
            default:
                translation = "Invalid type.";
                break;
        }
        return translation[0].toUpperCase() + translation.substring(1);
    }

    public emailTypeIconNameResolver = (mailPropertyName: string) => {
        switch (mailPropertyName.toLowerCase()) {
            case "date":
                return "DateTime";
            case "received":
                return "DateTime";
            case "attachments":
                return "CheckboxComposite";
            default:
                return "TextField";
        }
    }
}

const emailMapper = new EmailMapper();
export default emailMapper;